import { ImageBannerType } from 'types/graphql';

type ImageBannerStep = {
  description?: string;
  key: ImageBannerStepsEnums;
  title: string;
};

export enum ImageBannerStepsEnums {
  type = 'type',
  connection = 'connection',
  configuration = 'configuration',
  schedule = 'schedule',
  confirm = 'confirm',
}

export enum ImageBannerStepsStatus {
  current = 'current',
  complete = 'complete',
  incomplete = 'incomplete',
}

const imageBannerStepType: ImageBannerStep = {
  description: 'Select the type of image banner you want to create.',
  key: ImageBannerStepsEnums.type,
  title: 'Type',
};

const imageBannerStepConnection: ImageBannerStep = {
  description: 'Select the item to connect to your image banner.',
  key: ImageBannerStepsEnums.connection,
  title: 'Connection',
};

const imageBannerStepConfiguration: ImageBannerStep = {
  description: 'Select the platforms and images for your image banner.',
  key: ImageBannerStepsEnums.configuration,
  title: 'Image Banner Details',
};

const imageBannerStepSchedule: ImageBannerStep = {
  description: 'Select when the image banner should be published and define the schedule.',
  key: ImageBannerStepsEnums.schedule,
  title: 'Schedule',
};

const imageBannerStepConfirm: ImageBannerStep = {
  description: 'Review your image banner choices and publish to your menu.',
  key: ImageBannerStepsEnums.confirm,
  title: 'Confirm',
};

export const imageBannerSteps: ImageBannerStep[] = [
  imageBannerStepType,
  imageBannerStepConnection,
  imageBannerStepConfiguration,
  imageBannerStepSchedule,
  imageBannerStepConfirm,
];

/**
 * Returns the image banner steps with optional steps based on feature flags and banner type.
 * Schedule step is always included (image banner schedule is GA).
 * @param bannerType - The type of banner being created/edited
 * @param includeTypeStep - Whether to include the type selection step (controlled by connected-banners feature flag)
 */
export function getImageBannerSteps(bannerType?: ImageBannerType, includeTypeStep = true): ImageBannerStep[] {
  const steps: ImageBannerStep[] = [];

  // Only include type step when connected banners feature is enabled
  if (includeTypeStep) {
    steps.push(imageBannerStepType);
  }

  // Connection step is only needed for non-link banner types (and only when type step is shown)
  const needsConnection = includeTypeStep && bannerType && bannerType !== ImageBannerType.link;
  if (needsConnection) {
    steps.push(imageBannerStepConnection);
  }

  steps.push(imageBannerStepConfiguration);
  steps.push(imageBannerStepSchedule);
  steps.push(imageBannerStepConfirm);

  return steps;
}
